import { useState, useMemo, useEffect } from 'react';

import type { GridPaginationModel } from '@mui/x-data-grid';

import { useGetPaginatedDocumentsQuery } from '@/gql/operations';

import type { ParsedIManageDocument } from '../useParseRecords';

const DEFAULT_PAGE_SIZE = 50;

export const usePaginatedDocuments = (clientNumber: string | undefined, searchTerm?: string) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Reset to first page when search term changes
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchTerm]);

  const where = useMemo(() => {
    if (!searchTerm?.trim()) return undefined;
    const pattern = `%${searchTerm.trim()}%`;
    return {
      _or: [{ name: { _ilike: pattern } }, { doc_id: { _ilike: pattern } }],
    };
  }, [searchTerm]);

  const { data, loading, previousData } = useGetPaginatedDocumentsQuery({
    variables: {
      clientNumber: clientNumber!,
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
      where,
    },
    skip: !clientNumber,
  });

  const activeData = data ?? previousData;
  const engagementRecord = activeData?.mlfsl_client[0]?.cmap_clm_engagement_record;

  const rows: ParsedIManageDocument[] = useMemo(() => {
    const docs = engagementRecord?.engagement_imanage_documents ?? [];
    return docs.map((doc) => ({
      id: doc.id,
      docName: doc.name ?? doc.doc_id ?? 'Unknown Document',
      docId: doc.doc_id ?? 'N/A',
    }));
  }, [engagementRecord]);

  // Total count of matching records, independent of the current page.
  const rowCount = engagementRecord?.engagement_imanage_documents_aggregate?.aggregate?.count ?? 0;

  return {
    rows,
    rowCount,
    paginationModel,
    setPaginationModel,
    loading,
  };
};
